import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { MIN_SUPPORT_MESSAGE_LENGTH } from '@merchmuse/shared'
import { apiClient } from '@/lib/api-client'

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'account', label: 'Account Issue' },
  { value: 'other', label: 'Other' },
] as const

interface SupportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SupportDialog({ open, onOpenChange }: SupportDialogProps) {
  const [category, setCategory] = useState('other')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const canSubmit = message.trim().length >= MIN_SUPPORT_MESSAGE_LENGTH

  const handleSubmit = async () => {
    if (!canSubmit) return

    setLoading(true)
    try {
      await apiClient.submitSupportRequest({
        category,
        message: message.trim(),
        page_url: window.location.href,
      })
      toast.success("Support request sent. We'll get back to you via email.")
      setMessage('')
      setCategory('other')
      onOpenChange(false)
    } catch {
      toast.error('Failed to send support request. Please try emailing support@merchmuse.art directly.')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (value: boolean) => {
    if (loading) return
    onOpenChange(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Contact Support</DialogTitle>
          <DialogDescription>
            Describe your issue and we'll respond via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="support-category">Category</Label>
            <Select value={category} onValueChange={setCategory} disabled={loading}>
              <SelectTrigger id="support-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-message">Message</Label>
            <Textarea
              id="support-message"
              placeholder="Please describe your issue in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              rows={6}
              maxLength={5000}
            />
            <p className="text-xs text-muted-foreground">
              {message.length}/5000 characters (minimum 10)
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={loading || !canSubmit}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
